import { createAsyncThunk } from '@reduxjs/toolkit'
import { NotificationModel } from '../../models/Notification'
import { pushNotification } from '../application/applicationSlice'
import {
    profileRequest,
    profileUpdateRequest,
    tokenRefresh,
    tokenRequest,
    avatarUploadRequest,
} from '../../apiClient/userRequests'
import {
    isTokenValid,
    shouldRefreshAccessToken,
} from '../../apiClient/tokenUtils'
import i18n from '../../i18n'

let tokenRefreshPromise = null;
let authGeneration = 0;

const assertCurrentSession = (generation) => {
    if (generation !== authGeneration) {
        throw createAuthError('session_changed', 'Session has ended or changed');
    }
};

const httpStatus = (err) =>
  err?.response?.status ?? err?.status ?? err?.code ?? 0;

const httpDetail = (err) =>
  err?.response?.data ?? err?.message ?? 'Unknown error';

const clearPersistedAuth = () => {
    localStorage.removeItem('token_access');
    localStorage.removeItem('token_refresh');
};

const persistAuth = ({ access, refresh }) => {
    if (access) localStorage.setItem('token_access', access);
    if (refresh) localStorage.setItem('token_refresh', refresh);
};

const getAuthError = (err, fallbackCode = 'authentication_error') => ({
    http_status_code: httpStatus(err) || 401,
    detail: httpDetail(err),
    code: err?.code || err?.response?.data?.code || fallbackCode,
});

const createAuthError = (code, message) => {
    const error = new Error(message);
    error.code = code;
    error.status = 401;
    return error;
};

const refreshTokenPairOnce = (refresh) => {
    if (!tokenRefreshPromise) {
        const generation = authGeneration;
        const request = tokenRefresh({ refresh })
            .then((res) => {
                assertCurrentSession(generation);
                const data = res?.data;
                if (!data?.access) {
                    throw createAuthError(
                        'invalid_refresh_response',
                        'Token refresh response does not include an access token'
                    );
                }

                const tokens = {
                    access: data.access,
                    refresh: data.refresh || refresh,
                };
                persistAuth(tokens);
                return tokens;
            })
            .catch((err) => {
                assertCurrentSession(generation);
                throw err;
            })
            .finally(() => {
                if (tokenRefreshPromise === request) tokenRefreshPromise = null;
            });
        tokenRefreshPromise = request;
    }

    return tokenRefreshPromise;
};

export const initializeAuthThunk = createAsyncThunk(
    'user/initializeAuth',
    async (_, thunkAPI) => {
        const access = localStorage.getItem('token_access');
        const refresh = localStorage.getItem('token_refresh');

        if (!access && !refresh) {
            return thunkAPI.fulfillWithValue({ access: null, refresh: null });
        }

        if (!isTokenValid(refresh)) {
            clearPersistedAuth();
            return thunkAPI.rejectWithValue({
                http_status_code: 401,
                detail: 'Refresh token is invalid or expired',
                code: 'refresh_token_expired',
            });
        }

        if (!shouldRefreshAccessToken(access)) {
            return thunkAPI.fulfillWithValue({ access, refresh });
        }

        try {
            const tokens = await refreshTokenPairOnce(refresh);
            return thunkAPI.fulfillWithValue(tokens);
        } catch (err) {
            if (err.code !== 'session_changed') clearPersistedAuth();
            return thunkAPI.rejectWithValue(getAuthError(err));
        }
    }
);

export const ensureAccessTokenThunk = createAsyncThunk(
    'user/ensureAccessToken',
    async (
        {
            allowRefresh = false,
            forceRefresh = false,
            trigger = 'unknown',
        } = {},
        thunkAPI
    ) => {
        const state = thunkAPI.getState();
        const access = state.user.token_access;
        const refresh = state.user.token_refresh;

        if (tokenRefreshPromise) {
            try {
                const tokens = await tokenRefreshPromise;
                return thunkAPI.fulfillWithValue({
                    ...tokens,
                    refreshed: true,
                    trigger,
                });
            } catch (err) {
                if (err.code !== 'session_changed') clearPersistedAuth();
                return thunkAPI.rejectWithValue({
                    ...getAuthError(err),
                    trigger,
                });
            }
        }

        const refreshRequired =
            forceRefresh ||
            (allowRefresh
                ? shouldRefreshAccessToken(access)
                : !isTokenValid(access));

        if (!refreshRequired) {
            return thunkAPI.fulfillWithValue({
                access,
                refresh,
                refreshed: false,
                trigger,
            });
        }

        if (!isTokenValid(refresh)) {
            clearPersistedAuth();
            return thunkAPI.rejectWithValue({
                http_status_code: 401,
                detail: 'Refresh token is invalid or expired',
                code: 'refresh_token_expired',
                trigger,
            });
        }

        if (!allowRefresh) {
            return thunkAPI.rejectWithValue({
                http_status_code: 401,
                detail: 'Access token is invalid or expired',
                code: 'access_token_expired',
                trigger,
            });
        }

        try {
            const tokens = await refreshTokenPairOnce(refresh);
            return thunkAPI.fulfillWithValue({
                ...tokens,
                refreshed: true,
                trigger,
            });
        } catch (err) {
            if (err.code !== 'session_changed') clearPersistedAuth();
            return thunkAPI.rejectWithValue({
                ...getAuthError(err),
                trigger,
            });
        }
    }
);

export const loadPersistedAuthThunk = createAsyncThunk(
    'user/loadPersistedAuth',
    async () => {
        const access = localStorage.getItem('token_access');
        const refresh = localStorage.getItem('token_refresh');
        return { access, refresh };
    }
);

export const tokenRequestThunk = createAsyncThunk(
    'user/tokenRequest',
    async (loginForm, thunkAPI) => {
        const generation = ++authGeneration;
        tokenRefreshPromise = null;
        try {
            const res = await tokenRequest(loginForm);
            assertCurrentSession(generation);
            const data = res.data;
            if (data.access) localStorage.setItem('token_access', data.access);
            if (data.refresh) localStorage.setItem('token_refresh', data.refresh);
            return thunkAPI.fulfillWithValue(data);
        } catch (err) {
            return thunkAPI.rejectWithValue({
                http_status_code: httpStatus(err),
                detail: httpDetail(err),
            });
        }
    }
);

export const tokenRefreshThunk = createAsyncThunk(
    'user/tokenRefresh',
    async (_, thunkAPI) => {
        const state = thunkAPI.getState();
        try {
            if (!isTokenValid(state.user.token_refresh)) {
                throw createAuthError(
                    'refresh_token_expired',
                    'Refresh token is invalid or expired'
                );
            }

            const data = await refreshTokenPairOnce(state.user.token_refresh);
            return thunkAPI.fulfillWithValue(data);
        } catch (err) {
            if (err.code !== 'session_changed') clearPersistedAuth();
            return thunkAPI.rejectWithValue(getAuthError(err));
        }
    }
);

export const profileRequestThunk = createAsyncThunk(
    'user/profile',
    async (_, thunkAPI) => {
        try {
            const res = await profileRequest(thunkAPI);
            return thunkAPI.fulfillWithValue(res.data);
        } catch (err) {
            // hidrate error information on failed request
            return thunkAPI.rejectWithValue({
                http_status_code: httpStatus(err),
                detail: httpDetail(err),
            });
        }
    }
);
export const profileUpdateThunk = createAsyncThunk(
  'user/profile/update',
  async (payload, thunkAPI) => {
    try {
      const res = await profileUpdateRequest(thunkAPI, payload)
      return thunkAPI.fulfillWithValue(res.data)
    } catch (err) {
      thunkAPI.dispatch(
        pushNotification(
          new NotificationModel(
            i18n.t('profile.updateError'),
            i18n.t('common.httpError', { status: httpStatus(err) }),
            'error'
          ).toJson()
        )
      )
      return thunkAPI.rejectWithValue({
        http_status_code: httpStatus(err),
        detail: httpDetail(err),
      })
    }
  }
)
export const logoutThunk = createAsyncThunk(
    'user/logout',
    async (_, thunkAPI) => {
        try {
            authGeneration += 1;
            tokenRefreshPromise = null;
            clearPersistedAuth();
            document.cookie = `sessionid=; expires=Sat, 20 Jan 1980 12:00:00 UTC`;
            document.cookie = `csrftoken=; expires=Sat, 20 Jan 1980 12:00:00 UTC`;
            return thunkAPI.fulfillWithValue(true);
        } catch (error) {
            return thunkAPI.rejectWithValue(error);
        }
    }
)

export const avatarUploadThunk = createAsyncThunk(
    'user/avatar/upload',
    async (file, thunkAPI) => {
        try {
            const res = await avatarUploadRequest(thunkAPI, file)
            return thunkAPI.fulfillWithValue(res.data)
        } catch (err) {
            thunkAPI.dispatch(
                pushNotification(
                    new NotificationModel(
                        i18n.t('profile.avatarUploadError'),
                        i18n.t('common.httpError', { status: httpStatus(err) }),
                        'error'
                    ).toJson()
                )
            )
            return thunkAPI.rejectWithValue({
                http_status_code: httpStatus(err),
                detail: httpDetail(err),
            })
        }
    }
)
